// @flow

import { getApiBaseUrlByTenantCode, type TenantCode } from '@tectonics/commons/tenant';
import type { RiskSignals } from '../../pix-jsr-enrollment/hooks/use-risk-signals';
import type { AssertionOptions } from '../types';
import type { AssertionResponse } from './webauthn';

type QueryResponse = {|
  +status: string,
  +assertion_options?: AssertionOptions,
  +redirect_url_after_payment?: string,
  +status_message?: string,
|};

type AuthenticatePayload = {|
  +hash: string,
  +authentication: {|
    +credentialId: string,
    +rawCredentialId: string,
    +credentialType: string,
    +response: {|
      +clientDataJSON: string,
      +authenticatorData: string,
      +signature: string,
      +userHandle: string,
    |},
  |},
  +risk_signals: RiskSignals,
|};

type AuthenticateResponse = {|
  +status: string,
  +payment?: {|
    +hash: string,
  |},
  +status_code?: string,
  +status_message?: string,
|};

function getBaseUrl(tenantCode: TenantCode): string {
  return getApiBaseUrlByTenantCode(tenantCode);
}

export async function queryAssertionOptions(
  paymentHash: string,
  tenantCode: TenantCode,
): Promise<QueryResponse> {
  const baseUrl = getBaseUrl(tenantCode);
  const response = await fetch(
    `${baseUrl}cf/hosted-payment/query?hash=${encodeURIComponent(paymentHash)}`,
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.status_message || 'Falha ao consultar pagamento.');
  }

  return data;
}

export async function authenticatePayment(
  paymentHash: string,
  assertionData: AssertionResponse,
  riskSignals: RiskSignals,
  tenantCode: TenantCode,
): Promise<AuthenticateResponse> {
  const baseUrl = getBaseUrl(tenantCode);

  const payload: AuthenticatePayload = {
    hash: paymentHash,
    authentication: {
      credentialId: assertionData.credentialId,
      rawCredentialId: assertionData.rawCredentialId,
      credentialType: assertionData.credentialType,
      response: assertionData.response,
    },
    risk_signals: riskSignals,
  };

  const response = await fetch(
    `${baseUrl}cf/hosted-payment/authenticate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.status_message || 'Falha na autenticação do pagamento.');
  }

  return data;
}
